import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import MenuItem from "@material-ui/core/MenuItem";

import { i18n } from "../../translate/i18n";
import { Stack } from "@mui/material";

const MAX_INTERVAL_SECONDS = 24 * 60 * 60; // 24h

const UNIT_MULTIPLIER = {
  seconds: 1,
  minutes: 60,
  hours: 3600
};

const deriveUnitAmount = (sec) => {
  const s = Number(sec) || 0;
  if (s >= 3600 && s % 3600 === 0) {
    return { amount: s / 3600, unit: "hours" };
  }
  if (s >= 60 && s % 60 === 0) {
    return { amount: s / 60, unit: "minutes" };
  }
  return { amount: s, unit: "seconds" };
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },
  btnWrapper: {
    position: "relative"
  }
}));

const FlowBuilderIntervalModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [amount, setAmount] = useState(0);
  const [unit, setUnit] = useState("seconds");
  const [activeModal, setActiveModal] = useState(false);

  useEffect(() => {
    if (open === "edit") {
      const derived = deriveUnitAmount(data?.data?.sec);
      setAmount(derived.amount);
      setUnit(data?.data?.unit || derived.unit);
      setActiveModal(true);
    } else if (open === "create") {
      setAmount(10);
      setUnit("seconds");
      setActiveModal(true);
    }
    return () => {
      isMounted.current = false;
    };
  }, [open]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSaveContact = async () => {
    const value = parseFloat(String(amount));
    if (!value || value <= 0) {
      return toast.error("Adicione o valor de intervalo");
    }

    const multiplier = UNIT_MULTIPLIER[unit] || 1;
    const sec = Math.round(value * multiplier);

    if (sec > MAX_INTERVAL_SECONDS) {
      return toast.error("Máximo de tempo: 24 horas");
    }

    const payload = { sec, unit, amount: value };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: payload
      });
    } else if (open === "create") {
      onSave(payload);
    }
    handleClose();
  };

  const unitLabel =
    unit === "hours" ? "horas" : unit === "minutes" ? "minutos" : "segundos";

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {open === "create"
            ? "Adicionar um intervalo ao fluxo"
            : "Editar intervalo"}
        </DialogTitle>
        <Stack>
          <DialogContent dividers>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Tempo"
                name="timer"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
                variant="outlined"
                InputProps={{ inputProps: { min: 0, step: 1 } }}
                margin="dense"
                className={classes.textField}
                fullWidth
              />
              <TextField
                select
                label="Unidade"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                variant="outlined"
                margin="dense"
                style={{ minWidth: 160 }}
              >
                <MenuItem value="seconds">Segundos</MenuItem>
                <MenuItem value="minutes">Minutos</MenuItem>
                <MenuItem value="hours">Horas</MenuItem>
              </TextField>
            </Stack>
            <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
              Máximo: 24 horas. Valor em {unitLabel}.
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary" variant="outlined">
              {i18n.t("contactModal.buttons.cancel")}
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              className={classes.btnWrapper}
              onClick={() => handleSaveContact()}
            >
              {open === "create" ? "Adicionar" : "Editar"}
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderIntervalModal;
